// Boost Blenz Admin — SEO & Organic view.
//
// Live from three Google feeds via /api/seo: Search Console (organic search),
// GA4 (organic traffic + revenue), and Merchant Center (Shopping feed health) —
// plus a unified issues feed surfaced FIRST so what needs fixing reads at a
// glance. The Ahrefs "keyword opportunity" panel is a connect-later placeholder
// until the owner wires Ahrefs (then it becomes an imported target-keyword
// tracker that free Search Console tracks progress against).

import React from "react";
import { OSWALD, FIGTREE, MONO, useT, useIsMobile } from "../theme";
import { Card, Title, Eyebrow, Badge, Icon, Stat, Btn, SectionTitle } from "../atoms";
import { useCachedJson } from "../datacache";

const nf = (n: any) => (n == null ? "—" : Number(n).toLocaleString("en-US"));
const money = (n: any) => (n == null ? "—" : "$" + Number(n).toLocaleString("en-US", { maximumFractionDigits: 0 }));
const shortDate = (ymd: string) => (ymd && ymd.length >= 10 ? `${+ymd.slice(5, 7)}/${+ymd.slice(8, 10)}` : ymd);

// A source pill in the connection strip.
function Conn({ label, state, note }: { label: string; state: "live" | "warn" | "off"; note?: string }) {
  const t = useT();
  const c = state === "live" ? t.good : state === "warn" ? t.warn : t.faint;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 7, background: t.surface, border: "1px solid " + t.border, borderRadius: 999, padding: "6px 12px", fontFamily: FIGTREE, fontWeight: 600, fontSize: 12, color: state === "off" ? t.faint : t.fg2, borderStyle: state === "off" ? "dashed" : "solid" }}>
      <span style={{ width: 8, height: 8, borderRadius: 999, background: c, boxShadow: state === "off" ? "none" : `0 0 0 3px ${state === "live" ? t.goodSoft : t.warnSoft}` }} />
      {label}{note ? <span style={{ color: t.muted, fontWeight: 500 }}>· {note}</span> : null}
    </span>
  );
}

// One row in the issues feed. Badge sits ABOVE the title (its own line) so long
// source tags like "Search Console" never overlap the text, and the layout stacks
// cleanly on mobile — action button wraps below the copy when space is tight.
function IssueRow({ iss, first }: { iss: any; first: boolean }) {
  const t = useT();
  const isMobile = useIsMobile();
  const col = iss.severity === "critical" ? t.bad : iss.severity === "warning" ? t.warn : t.good;
  const tone = iss.severity === "critical" ? "bad" : iss.severity === "warning" ? "warn" : "good";
  const pill = (label: string) => (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontFamily: FIGTREE, fontWeight: 700, fontSize: 11.5, color: t.accent, border: "1px solid " + t.borderStrong, borderRadius: 999, padding: "6px 11px", whiteSpace: "nowrap" }}>{label} <Icon name="chevron" size={12} color={t.accent} /></span>
  );
  const action = iss.threadId ? (
    <a href={`https://mail.google.com/mail/u/0/#inbox/${iss.threadId}`} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>{pill("Open email")}</a>
  ) : iss.source === "Merchant" && iss.severity !== "ok" ? (
    <a href="https://merchants.google.com/" target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>{pill("Fix in Merchant")}</a>
  ) : null;
  return (
    <div style={{ display: "flex", gap: 12, alignItems: "stretch", padding: "13px 0", borderTop: first ? "none" : "1px solid " + t.border }}>
      <span style={{ width: 4, alignSelf: "stretch", minHeight: 34, borderRadius: 4, background: col, flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0, display: "flex", gap: 12, alignItems: "flex-start", justifyContent: "space-between", flexWrap: isMobile ? "wrap" : "nowrap" }}>
        <div style={{ flex: "1 1 auto", minWidth: 0 }}>
          <Badge tone={tone as any}>{iss.source}</Badge>
          <div style={{ fontFamily: FIGTREE, fontWeight: 700, fontSize: 14, color: t.fg, lineHeight: 1.3, marginTop: 7 }}>{iss.title}</div>
          {iss.detail ? <div style={{ fontFamily: FIGTREE, fontSize: 12.5, color: t.muted, marginTop: 3, lineHeight: 1.5 }}>{iss.detail}</div> : null}
        </div>
        {action ? <div style={{ flexShrink: 0 }}>{action}</div> : null}
      </div>
    </div>
  );
}

function QueryTable({ rows, firstCol, brandTag, stripRoot }: { rows: any[]; firstCol: string; brandTag?: boolean; stripRoot?: boolean }) {
  const t = useT();
  const th: any = { textAlign: "left", color: t.faint, fontWeight: 700, fontSize: 10.5, letterSpacing: "0.06em", textTransform: "uppercase", padding: "0 8px 9px", fontFamily: FIGTREE };
  const td: any = { padding: "9px 8px", borderTop: "1px solid " + t.border, fontFamily: FIGTREE, fontSize: 13, fontVariantNumeric: "tabular-nums" };
  const isBrand = (s: string) => /boost\s*ble|boostble/i.test(s);
  // For the pages table: drop the protocol + host so the readable path shows ("/",
  // "/products/…"). Full URL stays in the title tooltip.
  const label = (s: string) => {
    if (!stripRoot) return s;
    try { const u = new URL(s); return (u.pathname || "/") + (u.search || ""); } catch { return s.replace(/^https?:\/\/[^/]+/i, "") || "/"; }
  };
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead><tr><th style={th}>{firstCol}</th><th style={{ ...th, textAlign: "right" }}>Clicks</th><th style={{ ...th, textAlign: "right" }}>Impr.</th><th style={{ ...th, textAlign: "right" }}>CTR</th><th style={{ ...th, textAlign: "right" }}>Pos</th></tr></thead>
        <tbody>
          {rows.slice(0, 8).map((r, i) => (
            <tr key={i}>
              <td title={r.key} style={{ ...td, color: t.fg, fontWeight: 600, maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", cursor: "default" }}>
                {label(r.key)}{brandTag && isBrand(r.key) ? <span style={{ marginLeft: 8, fontFamily: FIGTREE, fontSize: 10, fontWeight: 700, color: t.purple, textTransform: "uppercase", letterSpacing: "0.05em" }}>brand</span> : null}
              </td>
              <td style={{ ...td, textAlign: "right" }}>{nf(r.clicks)}</td>
              <td style={{ ...td, textAlign: "right", color: t.muted }}>{nf(r.impressions)}</td>
              <td style={{ ...td, textAlign: "right", color: t.muted }}>{r.ctr}%</td>
              <td style={{ ...td, textAlign: "right" }}><span style={{ fontFamily: MONO, fontWeight: 700, fontSize: 12, padding: "2px 7px", borderRadius: 6, background: r.position <= 3 ? t.goodSoft : t.surface2, color: r.position <= 3 ? t.good : t.fg2 }}>{Number(r.position).toFixed(1)}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Interactive daily-clicks chart: hover anywhere to read that day's exact numbers.
function SearchChart({ rows }: { rows: any[] }) {
  const t = useT();
  const [hi, setHi] = React.useState<number | null>(null);
  const ref = React.useRef<HTMLDivElement>(null);
  const n = rows.length;
  const clicks = rows.map((r) => r.clicks || 0);
  const max = Math.max(...clicks, 1);
  const W = 600, H = 156;
  const X = (i: number) => (n <= 1 ? W / 2 : (i / (n - 1)) * W);
  const Y = (v: number) => H - (v / max) * (H - 12) - 5;
  const line = clicks.map((v, i) => (i ? "L" : "M") + X(i).toFixed(1) + " " + Y(v).toFixed(1)).join(" ");
  const area = `${line} L${X(n - 1).toFixed(1)} ${H} L${X(0).toFixed(1)} ${H} Z`;
  const total = clicks.reduce((a, b) => a + b, 0);
  const peakI = clicks.indexOf(Math.max(...clicks));
  const readAt = (clientX: number) => {
    const el = ref.current; if (!el) return;
    const r = el.getBoundingClientRect();
    const frac = Math.min(1, Math.max(0, (clientX - r.left) / r.width));
    setHi(Math.round(frac * (n - 1)));
  };
  const onMove = (e: React.MouseEvent) => readAt(e.clientX);
  // Touch: tap or drag along the chart to read a day; the point stays put on lift.
  const onTouch = (e: React.TouchEvent) => { const p = e.touches[0]; if (p) readAt(p.clientX); };
  const ticks = n <= 1 ? [0] : [...new Set([0, Math.round((n - 1) * 0.25), Math.round((n - 1) * 0.5), Math.round((n - 1) * 0.75), n - 1])];
  const tipShift = (i: number) => (i < n * 0.15 ? "0" : i > n * 0.85 ? "-100%" : "-50%");
  return (
    <div>
      <div style={{ display: "flex", gap: 18, marginBottom: 12, flexWrap: "wrap", fontFamily: FIGTREE, fontSize: 12.5, color: t.muted }}>
        <span><b style={{ color: t.fg, fontVariantNumeric: "tabular-nums" }}>{nf(total)}</b> clicks over the window</span>
        {clicks.length ? <span>busiest day <b style={{ color: t.fg2 }}>{shortDate(rows[peakI].date)}</b> ({nf(clicks[peakI])})</span> : null}
        <span style={{ color: t.faint }}>hover to read any day</span>
      </div>
      <div ref={ref} onMouseMove={onMove} onMouseLeave={() => setHi(null)} onTouchStart={onTouch} onTouchMove={onTouch} style={{ position: "relative", width: "100%", height: H, cursor: "crosshair", touchAction: "pan-y" }}>
        <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ width: "100%", height: H, display: "block", overflow: "visible" }}>
          <defs><linearGradient id="seoclk" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor={t.accent} stopOpacity="0.22" /><stop offset="1" stopColor={t.accent} stopOpacity="0" /></linearGradient></defs>
          <line x1="0" y1={H - 0.5} x2={W} y2={H - 0.5} stroke={t.border} strokeWidth="1" vectorEffect="non-scaling-stroke" />
          <path d={area} fill="url(#seoclk)" />
          <path d={line} fill="none" stroke={t.accent} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
          {hi != null && <line x1={X(hi)} y1="0" x2={X(hi)} y2={H} stroke={t.borderStrong} strokeWidth="1" strokeDasharray="3 3" vectorEffect="non-scaling-stroke" />}
        </svg>
        {hi != null && <span style={{ position: "absolute", width: 9, height: 9, borderRadius: 999, background: t.accent, border: "2px solid " + t.surface, left: `${(X(hi) / W) * 100}%`, top: `${(Y(clicks[hi]) / H) * 100}%`, transform: "translate(-50%,-50%)", pointerEvents: "none" }} />}
        {hi != null && (
          <div style={{ position: "absolute", left: `${(X(hi) / W) * 100}%`, top: -6, transform: `translate(${tipShift(hi)},-100%)`, background: t.surface, border: "1px solid " + t.borderStrong, borderRadius: 10, padding: "8px 11px", boxShadow: "0 8px 24px rgba(0,0,0,0.3)", pointerEvents: "none", whiteSpace: "nowrap", zIndex: 5 }}>
            <div style={{ fontFamily: FIGTREE, fontWeight: 700, fontSize: 12, color: t.fg, marginBottom: 4 }}>{shortDate(rows[hi].date)}</div>
            <div style={{ fontFamily: FIGTREE, fontSize: 12, color: t.fg2, fontVariantNumeric: "tabular-nums", display: "flex", gap: 12 }}>
              <span><span style={{ color: t.accent }}>●</span> {nf(rows[hi].clicks)} clicks</span>
              <span style={{ color: t.muted }}>{nf(rows[hi].impressions)} impr.</span>
            </div>
          </div>
        )}
      </div>
      <div style={{ position: "relative", height: 18, marginTop: 7 }}>
        {ticks.map((ti) => (
          <span key={ti} style={{ position: "absolute", left: `${(X(ti) / W) * 100}%`, transform: `translateX(${ti === 0 ? "0" : ti === n - 1 ? "-100%" : "-50%"})`, fontFamily: FIGTREE, fontSize: 12, fontWeight: 600, color: t.muted, whiteSpace: "nowrap" }}>{shortDate(rows[ti].date)}</span>
        ))}
      </div>
    </div>
  );
}

export function SeoView() {
  const t = useT();
  const isMobile = useIsMobile();
  const [issuesOpen, setIssuesOpen] = React.useState(false);
  const { data, loading, error, refresh, revalidating } = useCachedJson<any>("/api/seo");

  if (loading && !data) {
    return (
      <div style={{ display: "grid", gap: 16 }}>
        <SectionTitle sub="Search visibility, organic traffic & feed health — every organic surface in one place.">SEO &amp; Organic</SectionTitle>
        <Card><div style={{ padding: "40px 0", textAlign: "center", color: t.muted, fontFamily: FIGTREE }}>Loading live search, traffic &amp; feed data…</div></Card>
      </div>
    );
  }
  if (error && !data) {
    return (
      <div style={{ display: "grid", gap: 16 }}>
        <SectionTitle sub="Search visibility, organic traffic & feed health.">SEO &amp; Organic</SectionTitle>
        <Card><div style={{ padding: "30px 0", textAlign: "center", color: t.bad, fontFamily: FIGTREE }}>Couldn't load SEO data. <button onClick={refresh} style={{ color: t.accent, background: "none", border: "none", cursor: "pointer", fontWeight: 700 }}>Retry</button></div></Card>
      </div>
    );
  }

  const d = data || {};
  const gsc = d.gsc || {};
  const gscOk = gsc.connected && !gsc.error;
  const totals = gsc.totals || {};
  const organic = d.organic;
  const mc = d.merchant || {};
  const mcOk = mc.connected && !mc.error;
  const stats = mc.stats || { active: 0, disapproved: 0, pending: 0, total: 0 };
  const issues: any[] = d.issues || [];
  const counts = d.counts || { critical: 0, warning: 0 };

  const tileCols = isMobile ? "repeat(2,1fr)" : "repeat(6,1fr)";
  const twoCol = (a: string) => ({ display: "grid", gap: 16, gridTemplateColumns: isMobile ? "1fr" : a });

  const activePct = stats.total > 0 ? Math.round((stats.active / stats.total) * 100) : 0;
  const disPct = stats.total > 0 ? Math.round((stats.disapproved / stats.total) * 100) : 0;

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <SectionTitle
        sub="Search visibility, organic traffic & feed health — with what needs fixing surfaced first."
        right={<div style={{ display: "flex", alignItems: "center", gap: 10 }}>{revalidating ? <span style={{ fontFamily: FIGTREE, fontSize: 11.5, color: t.faint }}>updating…</span> : null}<Btn variant="ghost" size="sm" icon="refresh" onClick={refresh}>Refresh</Btn></div>}
      >SEO &amp; Organic</SectionTitle>

      {/* connection strip */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <Conn label="Search Console" state={gsc.connected ? (gsc.error ? "warn" : "live") : "off"} />
        <Conn label="GA4" state={d.ga4?.connected ? (d.ga4.error ? "warn" : "live") : "off"} />
        <Conn label="Merchant Center" state={mc.connected ? (stats.disapproved > 0 ? "warn" : "live") : "off"} note={mcOk ? `${stats.disapproved} disapproved` : undefined} />
        <Conn label="Ahrefs" state="off" note="connect to unlock keyword gap" />
      </div>

      {/* ISSUES FEED — collapsed on open; tap the header to expand */}
      <Card>
        <div onClick={() => setIssuesOpen((o) => !o)} role="button" aria-expanded={issuesOpen} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap", cursor: "pointer", userSelect: "none" }}>
          <Title size={17} style={{ display: "flex", alignItems: "center", gap: 9 }}><Icon name="alert" size={18} color={t.purple} />Issues to fix</Title>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {counts.critical > 0 ? <Badge tone="bad">{counts.critical} critical</Badge> : null}
            {counts.warning > 0 ? <Badge tone="warn">{counts.warning} warning{counts.warning === 1 ? "" : "s"}</Badge> : null}
            {counts.critical === 0 && counts.warning === 0 ? <Badge tone="good">All clear</Badge> : null}
            <Icon name="chevron" size={16} color={t.muted} style={{ transform: issuesOpen ? "rotate(90deg)" : "none", transition: "transform 0.15s", flexShrink: 0 }} />
          </div>
        </div>
        {issuesOpen ? (
          <div style={{ marginTop: 6 }}>
            {issues.length ? issues.map((iss, i) => <IssueRow key={iss.id || i} iss={iss} first={i === 0} />)
              : <div style={{ padding: "18px 0", color: t.muted, fontFamily: FIGTREE }}>No issues detected across your organic surfaces.</div>}
          </div>
        ) : null}
      </Card>

      {/* KPI TILES */}
      <div style={{ display: "grid", gap: 12, gridTemplateColumns: tileCols }}>
        <Stat label="Clicks · Search Console" value={gscOk ? nf(totals.clicks) : "—"} sub="last 28 days" />
        <Stat label="Impressions · GSC" value={gscOk ? nf(totals.impressions) : "—"} sub="last 28 days" />
        <Stat label="Click-through rate · GSC" value={gscOk ? `${totals.ctr}%` : "—"} sub="organic search" />
        <Stat label="Avg. position · GSC" value={gscOk ? Number(totals.position).toFixed(1) : "—"} sub="lower is better" />
        <Stat label="Organic sessions · GA4" value={organic ? nf(organic.sessions) : "—"} sub="last 30 days" />
        <Stat label="Organic revenue · GA4" value={organic ? money(organic.revenue) : "—"} sub="GA4-attributed" />
      </div>

      {/* TREND + BRANDED SPLIT  |  FEED HEALTH */}
      <div style={twoCol("1.6fr 1fr") as any}>
        <Card>
          <SectionTitle sub="Clicks from Google Search, per day">Search performance</SectionTitle>
          {(gsc.byDate || []).length > 1 ? <SearchChart rows={gsc.byDate} />
            : <div style={{ padding: "30px 0", textAlign: "center", color: t.muted, fontFamily: FIGTREE }}>Not enough data yet.</div>}
          <div style={{ borderTop: "1px solid " + t.border, marginTop: 16, paddingTop: 16 }}>
            <Eyebrow>Where your search traffic comes from</Eyebrow>
            <div style={{ fontFamily: FIGTREE, fontSize: 13, color: t.fg2, margin: "6px 0 13px", lineHeight: 1.5 }}>
              {d.brandedPct != null ? <><b style={{ color: t.fg }}>{d.brandedPct}%</b> is people searching your brand name (you rank #1). The growth lane is the non-branded, category searches that reach new customers.</> : "Connect data to see the branded split."}
            </div>
            {d.brandedPct != null ? (
              <>
                <div style={{ display: "flex", height: 22, borderRadius: 7, overflow: "hidden", border: "1px solid " + t.border }}>
                  <div style={{ width: `${Math.max(d.brandedPct, 1)}%`, background: t.purple }} />
                  <div style={{ width: `${Math.max(100 - d.brandedPct, 0)}%`, background: t.surface2 }} />
                </div>
                <div style={{ display: "flex", gap: 20, marginTop: 12, flexWrap: "wrap", fontFamily: FIGTREE, fontSize: 12.5 }}>
                  <span style={{ display: "flex", alignItems: "center", gap: 8, color: t.fg2 }}>
                    <span style={{ width: 11, height: 11, borderRadius: 3, background: t.purple, flexShrink: 0 }} />
                    <b style={{ color: t.fg }}>Branded {d.brandedPct}%</b> <span style={{ color: t.muted }}>· searching your name ({nf(d.brandedClicks)} clicks)</span>
                  </span>
                  <span style={{ display: "flex", alignItems: "center", gap: 8, color: t.fg2 }}>
                    <span style={{ width: 11, height: 11, borderRadius: 3, background: t.surface2, border: "1px solid " + t.borderStrong, flexShrink: 0 }} />
                    <b style={{ color: t.fg }}>Non-branded {100 - d.brandedPct}%</b> <span style={{ color: t.muted }}>· category searches ({nf(d.nonBrandedClicks)} clicks)</span>
                  </span>
                </div>
              </>
            ) : null}
          </div>
        </Card>

        <Card>
          <SectionTitle sub={mcOk ? `${nf(stats.total)} products in the feed` : "Google Shopping feed"}>Shopping feed health</SectionTitle>
          {mcOk ? (
            <>
              <div style={{ display: "flex", height: 32, borderRadius: 9, overflow: "hidden", border: "1px solid " + t.border, marginBottom: 14 }}>
                <div style={{ width: `${activePct}%`, background: t.good }} />
                <div style={{ width: `${disPct}%`, background: t.bad }} />
                <div style={{ flex: 1, background: t.surface2 }} />
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
                {[["Active", stats.active, t.good], ["Disapproved", stats.disapproved, t.bad], ["Pending", stats.pending || 0, t.faint]].map(([lab, val, col]: any) => (
                  <div key={lab} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontFamily: FIGTREE, fontSize: 13 }}>
                    <span style={{ display: "flex", alignItems: "center", gap: 8, color: t.fg2 }}><span style={{ width: 10, height: 10, borderRadius: 3, background: col }} />{lab}</span>
                    <b style={{ fontVariantNumeric: "tabular-nums" }}>{nf(val)}</b>
                  </div>
                ))}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 14 }}>
                {(mc.itemIssues || []).slice(0, 3).map((it: any, i: number) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, background: t.surface2, border: "1px solid " + t.border, borderRadius: 10, padding: "9px 11px" }}>
                    <span style={{ fontFamily: FIGTREE, fontSize: 12.5, color: t.fg2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{it.description}</span>
                    <Badge tone={it.servability === "disapproved" ? "bad" : "warn"}>{it.numItems}</Badge>
                  </div>
                ))}
              </div>
            </>
          ) : <div style={{ padding: "24px 0", color: t.muted, fontFamily: FIGTREE, textAlign: "center" }}>{mc.connected ? "Merchant Center isn't responding right now." : "Merchant Center not connected."}</div>}
        </Card>
      </div>

      {/* QUERIES + PAGES */}
      <div style={twoCol("1fr 1fr") as any}>
        <Card>
          <SectionTitle sub="What people search to find you">Top search queries</SectionTitle>
          {gscOk && (gsc.topQueries || []).length ? <QueryTable rows={gsc.topQueries} firstCol="Query" brandTag /> : <div style={{ padding: "20px 0", color: t.muted, fontFamily: FIGTREE }}>No query data.</div>}
        </Card>
        <Card>
          <SectionTitle sub="Pages earning organic clicks">Top landing pages</SectionTitle>
          {gscOk && (gsc.topPages || []).length ? <QueryTable rows={gsc.topPages} firstCol="Page" stripRoot /> : <div style={{ padding: "20px 0", color: t.muted, fontFamily: FIGTREE }}>No page data.</div>}
        </Card>
      </div>

      {/* AHREFS — connect later */}
      <Card style={{ border: "1px dashed " + t.borderStrong, background: "linear-gradient(160deg,rgba(185,169,255,0.05),rgba(248,146,92,0.04))" }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <div>
            <Title size={17} style={{ display: "flex", alignItems: "center", gap: 9 }}><Icon name="bullseye" size={18} color={t.purple} />Keyword opportunity &amp; competitor gap <Badge tone="purple" style={{ marginLeft: 6 }}>Ahrefs</Badge></Title>
            <div style={{ fontFamily: FIGTREE, fontSize: 13, color: t.muted, marginTop: 8, maxWidth: "72ch", lineHeight: 1.55 }}>
              Search Console shows the terms you <b style={{ color: t.fg2 }}>already</b> appear for — all branded. To find the non-branded, easy-win keywords your competitors rank for and you don't (plus difficulty scores and backlinks), we pull an Ahrefs snapshot in, then free Search Console tracks your climb against it year-round.
            </div>
          </div>
          <Btn variant="soft" tone="purple" icon="plug" disabled>Connect Ahrefs (next)</Btn>
        </div>
        <div style={{ display: "grid", gap: 12, gridTemplateColumns: isMobile ? "repeat(2,1fr)" : "repeat(4,1fr)", marginTop: 16 }}>
          {[["Competitor keyword gap", "keywords rivals rank for, you don't"], ["Easy wins (low difficulty)", "your fastest ranking targets"], ["Backlink gap", "domain-authority signal"], ["Live rank tracking", "via free Search Console"]].map(([lab, sub]: any) => (
            <div key={lab} style={{ background: t.surface2, border: "1px solid " + t.border, borderRadius: 12, padding: 13 }}>
              <div style={{ fontFamily: FIGTREE, fontSize: 11, letterSpacing: "0.04em", textTransform: "uppercase", color: t.muted, fontWeight: 700 }}>{lab}</div>
              <div style={{ fontFamily: OSWALD, fontWeight: 700, fontSize: 22, color: t.faint, marginTop: 8 }}>— · —</div>
              <div style={{ fontFamily: FIGTREE, fontSize: 11, color: t.faint, marginTop: 3 }}>{sub}</div>
            </div>
          ))}
        </div>
      </Card>

      <div style={{ display: "flex", gap: 11, alignItems: "flex-start", background: t.surface2, border: "1px solid " + t.border, borderRadius: t.cardRadius, padding: "13px 15px" }}>
        <Icon name="bell" size={16} color={t.muted} style={{ marginTop: 2, flexShrink: 0 }} />
        <div style={{ fontFamily: FIGTREE, fontSize: 12.5, color: t.muted, lineHeight: 1.55 }}>
          <b style={{ color: t.fg2 }}>Alerts:</b> every issue above also flows into your Daily Pulse active-issues feed, so a new disapproval or a Core Web Vitals drop reaches you on the morning brief — no need to remember to check this tab.
        </div>
      </div>
    </div>
  );
}
